import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';

import { CampaignsService } from 'app/core/services/campaigns.service';
import {
  Campaign,
  CampaignFormMode,
  CampaignFormModalData,
  CampaignFormModalResult,
  CampaignStatus,
  CAMPAIGN_DESCRIPTION_MAX_LENGTH,
  CAMPAIGN_IMAGE_ACCEPTED_MIME_TYPES,
  CAMPAIGN_IMAGE_ACCEPT_ATTRIBUTE,
  CAMPAIGN_IMAGE_MAX_SIZE_BYTES,
  CAMPAIGN_IMAGE_MAX_SIZE_MB,
  CAMPAIGN_DIALOG_PANEL_CLASS,
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGN_TITLE_MAX_LENGTH,
  CAMPAIGN_URL_MAX_LENGTH,
  hasAcceptedImageExtension,
  isValidCampaignUrl,
} from 'app/shared/models/campaign';

const BLOB_URL_PREFIX = 'blob:';
/** Splits a URL on its query string or fragment, keeping only the path. */
const URL_QUERY_SEPARATORS = /[?#]/;
const CONFLICT_STATUS = 409;
const URL_INVALID_ERROR = 'campaignUrl';

const ERROR_IMAGE_REQUIRED = 'support-campaigns.form.image-required';
const ERROR_IMAGE_INVALID_TYPE = 'support-campaigns.form.image-invalid-type';
const ERROR_IMAGE_TOO_LARGE = 'support-campaigns.form.image-too-large';

/**
 * Rejects a value made only of whitespace.
 *
 * @param control - Control holding the raw typed value.
 * @returns The `required` error, so the existing per-field messages cover it.
 *
 * @remarks
 * `Validators.required` counts `'   '` as filled, which defeats a mandatory field
 * in two different ways: a blank title reaches the backend, which trims it and
 * answers 400, and a blank URL is normalized to `null` on the way out — stored as
 * "no URL" on a campaign that is supposed to always have one.
 */
export function notBlankValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;

  if (typeof value !== 'string') {
    return null;
  }

  if (value.trim().length === 0) {
    return { required: true };
  }

  return null;
}

/**
 * Validates the shape of the campaign URL, leaving emptiness to `required`.
 *
 * @remarks
 * Judges only what was actually typed, so a blank field yields the single
 * `required` error instead of stacking a format complaint on top of it.
 */
export function campaignUrlValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;

  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }

  if (isValidCampaignUrl(value.trim())) {
    return null;
  }

  return { [URL_INVALID_ERROR]: true };
}

@Component({
  selector: 'app-campaign-form-modal',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './campaign-form-modal.component.html',
  styleUrls: ['./campaign-form-modal.component.scss'],
})
export class CampaignFormModalComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly campaignsService = inject(CampaignsService);
  private readonly dialogRef =
    inject<MatDialogRef<CampaignFormModalComponent>>(MatDialogRef);
  private readonly data = inject<CampaignFormModalData>(MAT_DIALOG_DATA);

  campaignForm: FormGroup;

  readonly mode: CampaignFormMode = this.data.mode;

  readonly statusOptions = CAMPAIGN_STATUS_OPTIONS;

  readonly titleMaxLength = CAMPAIGN_TITLE_MAX_LENGTH;
  readonly descriptionMaxLength = CAMPAIGN_DESCRIPTION_MAX_LENGTH;
  readonly urlMaxLength = CAMPAIGN_URL_MAX_LENGTH;
  readonly imageMaxSizeMb = CAMPAIGN_IMAGE_MAX_SIZE_MB;
  readonly imageAcceptAttribute = CAMPAIGN_IMAGE_ACCEPT_ATTRIBUTE;

  /** URL rendered in the image preview — either persisted or a local object URL. */
  previewUrl: string | null = null;

  /** Name shown next to the preview. Empty when it must fall back to a label. */
  previewFileName = '';

  /** Set when the image validation rejects the picked file, or none was picked. */
  imageErrorKey: string | null = null;

  /** Set when the backend rejects the submit for a reason worth showing inline. */
  submitErrorKey: string | null = null;

  isSubmitting = false;

  private readonly campaign?: Campaign = this.data.campaign;
  private selectedFile: File | null = null;
  private readonly localObjectUrls: string[] = [];
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.campaignForm = this.fb.group({
      title: [
        '',
        [
          Validators.required,
          notBlankValidator,
          Validators.maxLength(this.titleMaxLength),
        ],
      ],
      description: [
        '',
        [
          Validators.required,
          notBlankValidator,
          Validators.maxLength(this.descriptionMaxLength),
        ],
      ],
      status: [CampaignStatus.Active, Validators.required],
      url: [
        '',
        [
          Validators.required,
          notBlankValidator,
          Validators.maxLength(this.urlMaxLength),
          campaignUrlValidator,
        ],
      ],
      // Not required: a campaign that stays out of the carousel is a valid
      // campaign, so `false` is an answer rather than a missing value.
      announceOnLogin: [false],
    });

    if (this.campaign) {
      this.campaignForm.patchValue({
        title: this.campaign.title,
        description: this.campaign.description,
        status: this.campaign.status,
        url: this.campaign.url ?? '',
        announceOnLogin: this.campaign.announceOnLogin === true,
      });
      this.previewUrl = this.campaign.imageUrl;
      this.previewFileName = this.resolvePersistedFileName(
        this.campaign.imageUrl,
      );
    }

    if (this.isViewMode) {
      this.campaignForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get titleControl() {
    return this.campaignForm.get('title');
  }

  get descriptionControl() {
    return this.campaignForm.get('description');
  }

  get descriptionLength(): number {
    const value = this.descriptionControl?.value;

    if (!value) {
      return 0;
    }

    return value.length;
  }

  get showTitleRequiredError(): boolean {
    const control = this.titleControl;

    if (!control) {
      return false;
    }

    return control.hasError('required') && (control.dirty || control.touched);
  }

  get showDescriptionRequiredError(): boolean {
    const control = this.descriptionControl;

    if (!control) {
      return false;
    }

    return control.hasError('required') && (control.dirty || control.touched);
  }

  get urlControl() {
    return this.campaignForm.get('url');
  }

  get showUrlRequiredError(): boolean {
    const control = this.urlControl;

    if (!control) {
      return false;
    }

    return control.hasError('required') && (control.dirty || control.touched);
  }

  get showUrlInvalidError(): boolean {
    const control = this.urlControl;

    if (!control) {
      return false;
    }

    const isInvalid =
      control.hasError(URL_INVALID_ERROR) || control.hasError('maxlength');

    return isInvalid && (control.dirty || control.touched);
  }

  /**
   * Tells whether the campaign still has no image.
   *
   * @remarks
   * Reads the preview rather than the picked file, so an image already stored on
   * the campaign counts: editing a campaign must not force a re-upload.
   */
  get isImageMissing(): boolean {
    return !this.previewUrl;
  }

  /**
   * Tells whether the form may be submitted.
   *
   * @remarks
   * The image is not a form control — it is uploaded in a second request — so
   * form validity alone would let a campaign through without one. Every field is
   * mandatory, so this is also what keeps the submit button disabled until the
   * author has filled the form in.
   */
  get canSubmit(): boolean {
    if (this.isViewMode || this.isSubmitting) {
      return false;
    }

    if (this.isImageMissing || this.imageErrorKey) {
      return false;
    }

    return this.campaignForm.valid;
  }

  /** Heading shown in the modal — the campaign title when only viewing. */
  get headingKeyOrText(): string {
    if (this.mode === 'create') {
      return 'support-campaigns.form.heading-create';
    }

    if (this.mode === 'edit') {
      return 'support-campaigns.form.heading-edit';
    }

    return '';
  }

  get viewHeadingText(): string {
    return this.campaign?.title ?? '';
  }

  /** Resolves the i18n key for a status option. */
  statusLabelKey(status: CampaignStatus): string {
    if (status === CampaignStatus.Active) {
      return 'support-campaigns.status.active';
    }

    if (status === CampaignStatus.Paused) {
      return 'support-campaigns.status.paused';
    }

    return 'support-campaigns.status.inactive';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.imageErrorKey = null;

    // Both checks, because either one alone is fooled: a file the browser could
    // not type arrives with an empty `type`, and a renamed one arrives typed.
    const isAcceptedType =
      CAMPAIGN_IMAGE_ACCEPTED_MIME_TYPES.includes(file.type) &&
      hasAcceptedImageExtension(file.name);

    if (!isAcceptedType) {
      this.imageErrorKey = ERROR_IMAGE_INVALID_TYPE;
      input.value = '';
      return;
    }

    if (file.size > CAMPAIGN_IMAGE_MAX_SIZE_BYTES) {
      this.imageErrorKey = ERROR_IMAGE_TOO_LARGE;
      input.value = '';
      return;
    }

    this.discardLocalPreview();

    const objectUrl = URL.createObjectURL(file);
    this.localObjectUrls.push(objectUrl);

    this.selectedFile = file;
    this.previewUrl = objectUrl;
    this.previewFileName = file.name;

    // Allows picking the same file again after a removal.
    input.value = '';
  }

  /**
   * Drops the current image.
   *
   * @remarks
   * Leaves the required-image error behind on purpose: the campaign cannot be
   * saved without one, so the removal has to say what it now expects instead of
   * silently disabling the submit button.
   */
  removeImage(): void {
    this.discardLocalPreview();
    this.selectedFile = null;
    this.previewUrl = null;
    this.previewFileName = '';
    this.imageErrorKey = ERROR_IMAGE_REQUIRED;
  }

  close(): void {
    this.revokeLocalObjectUrls();
    this.dialogRef.close();
  }

  /**
   * Validates the form and sends it.
   *
   * @remarks
   * Re-checks everything the disabled submit button already guards: the form can
   * still be submitted by pressing Enter in a text field, so the button state is
   * an affordance, not the gate.
   */
  onSubmit(): void {
    if (this.isViewMode || this.isSubmitting) {
      return;
    }

    if (this.isImageMissing) {
      this.imageErrorKey = ERROR_IMAGE_REQUIRED;
    }

    if (this.campaignForm.invalid || !this.canSubmit) {
      // Touching every control is what makes the per-field messages appear;
      // until then a pristine field is empty but not yet complained about.
      this.campaignForm.markAllAsTouched();
      return;
    }

    this.submitErrorKey = null;
    this.isSubmitting = true;

    let request: Observable<Campaign>;

    if (this.isCreateMode) {
      request = this.createCampaign();
    } else {
      request = this.updateCampaign();
    }

    request
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: (campaign) => {
          const result: CampaignFormModalResult = { mode: this.mode, campaign };
          this.dialogRef.close(result);
        },
        error: (error: { status?: number }) => {
          // A duplicate title is the one failure the author can fix without
          // leaving the form, so it is surfaced inline instead of closing.
          if (error?.status === CONFLICT_STATUS) {
            this.submitErrorKey = 'support-campaigns.form.title-duplicate';
            return;
          }

          this.imageErrorKey = null;
          this.dialogRef.close();
        },
      });
  }

  /**
   * Creates the campaign and, when an image was picked, uploads it right after.
   *
   * @remarks
   * Two steps on purpose — it mirrors the Phase 2 contract, where the image is
   * sent to `POST /campaigns/{id}/image` only once the campaign has an id.
   */
  private createCampaign(): Observable<Campaign> {
    const { title, description, status } = this.campaignForm.getRawValue();

    return this.campaignsService
      .create({
        title,
        description,
        status,
        url: this.resolveUrlValue(),
        announceOnLogin: this.resolveAnnounceValue(),
      })
      .pipe(
        switchMap((created) => {
          if (!this.selectedFile) {
            return of(created);
          }

          return this.campaignsService
            .uploadImage(created.id, this.selectedFile)
            .pipe(switchMap((imageUrl) => of({ ...created, imageUrl })));
        }),
      );
  }

  /**
   * Saves the campaign, uploading the image first when a new one was picked.
   *
   * @remarks
   * Never sends `imageUrl: null`. An image is mandatory, so the only two states
   * that can reach here are "a new file replaces the stored one" and "the stored
   * one is untouched" — removing an image without picking another blocks the
   * submit rather than clearing it.
   */
  private updateCampaign(): Observable<Campaign> {
    const campaignId = this.campaign?.id ?? '';
    const { title, description, status } = this.campaignForm.getRawValue();
    const url = this.resolveUrlValue();
    const announceOnLogin = this.resolveAnnounceValue();

    if (this.selectedFile) {
      return this.campaignsService
        .uploadImage(campaignId, this.selectedFile)
        .pipe(
          switchMap((imageUrl) =>
            this.campaignsService.update(campaignId, {
              title,
              description,
              status,
              url,
              announceOnLogin,
              imageUrl,
            }),
          ),
        );
    }

    return this.campaignsService.update(campaignId, {
      title,
      description,
      status,
      url,
      announceOnLogin,
      imageUrl: this.campaign?.imageUrl ?? null,
    });
  }

  /** Reads the announce checkbox as a strict boolean. */
  private resolveAnnounceValue(): boolean {
    return this.campaignForm.getRawValue().announceOnLogin === true;
  }

  /**
   * Normalizes the typed URL for the API.
   *
   * @returns The trimmed address, or `null` when the field was left blank.
   *
   * @remarks
   * A blank field becomes `null` rather than `''` so a campaign that never had a
   * URL and one whose URL was cleared are stored identically.
   */
  private resolveUrlValue(): string | null {
    const value = this.urlControl?.value;

    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    return trimmed;
  }

  /**
   * Revokes the object URL created in this modal session, if the current preview
   * is one. Persisted URLs are left untouched.
   */
  private discardLocalPreview(): void {
    if (!this.previewUrl) {
      return;
    }

    if (!this.localObjectUrls.includes(this.previewUrl)) {
      return;
    }

    URL.revokeObjectURL(this.previewUrl);
  }

  private revokeLocalObjectUrls(): void {
    for (const objectUrl of this.localObjectUrls) {
      URL.revokeObjectURL(objectUrl);
    }
  }

  /**
   * Derives a display name from a persisted image URL.
   *
   * @remarks
   * Object URLs carry no file name, so the caller falls back to a translated
   * label whenever this returns an empty string.
   *
   * Blob Storage URLs come with a SAS query string appended, which would render
   * as a multi-line wall of text next to the preview, so the query and the
   * fragment are dropped before the last path segment is taken.
   */
  private resolvePersistedFileName(imageUrl: string | null): string {
    if (!imageUrl) {
      return '';
    }

    if (imageUrl.startsWith(BLOB_URL_PREFIX)) {
      return '';
    }

    const [pathWithoutQuery] = imageUrl.split(URL_QUERY_SEPARATORS);
    const segments = pathWithoutQuery.split('/');
    return segments[segments.length - 1];
  }
}
